// client/src/components/pages/WeighbridgeCompliance/OperatorManagement.tsx
// (converted from .js in the WBE batch, ST-00223)
//
// WBE-5: the "Assigned Assets" count and the expanded panel read
// `op.assignedAssets`, which no endpoint returns, so they always showed 0 / none.
// The list now reads the server's `assigned_assets`. WBE-13(b): the qualification
// column shows the label ("SCWO - SASCO Certified Weighbridge Operator"), not the
// stored code. The status filter offers Active / Inactive — what the modal writes.
import ThemedSelect from '../../common/ThemedSelect';
import { Fragment, useState, useEffect, useCallback } from 'react';
import {
  Users,
  UserPlus,
  Search,
  Filter,
  Edit2,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { parseISO, differenceInDays } from 'date-fns';
import type { AxiosResponse } from 'axios';
import { formatAppDate } from '../../../utils/dateUtils';
import api from '../../../services/weighbridgeComplianceApi';
import { OperatorModal, qualificationLabel } from './OperatorModal';
import type { OperatorRow, PaginatedBody } from '../../../types/clientPages/weighbridgeCompliance';

export const OperatorManagement = () => {
  const [operators, setOperators] = useState<OperatorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingOperator, setEditingOperator] = useState<OperatorRow | null>(null);
  const [selectedOperator, setSelectedOperator] = useState<OperatorRow | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchOperators = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (statusFilter !== 'All') params.status = statusFilter;
      if (debouncedSearchTerm) params.search = debouncedSearchTerm;
      const response = (await api.operators.getAll(params)) as AxiosResponse<
        PaginatedBody<OperatorRow> | OperatorRow[]
      >;
      const d = response.data;
      setOperators(Array.isArray(d) ? d : d?.data || []);
    } catch (error) {
      // One message per failing endpoint, however often the search reloads it (ST-00259).
      toast.error('Failed to load operators', { toastId: 'compliance-operators-load' });
      console.error('Error fetching operators:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, debouncedSearchTerm]);

  useEffect(() => {
    fetchOperators();
  }, [fetchOperators]);

  const handleDelete = async (id: number) => {
    try {
      await api.operators.delete(id);
      toast.success('Operator deleted successfully');
      setDeleteConfirm(null);
      fetchOperators();
    } catch (error) {
      toast.error('Failed to delete operator');
      console.error('Error deleting operator:', error);
    }
  };

  const handleEdit = (operator: OperatorRow) => {
    setEditingOperator(operator);
    setModalOpen(true);
  };

  const handleAdd = () => {
    setEditingOperator(null);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingOperator(null);
  };

  const handleModalSave = () => {
    handleModalClose();
    fetchOperators();
  };

  const handleRowClick = (operator: OperatorRow) => {
    setSelectedOperator(selectedOperator?.id === operator.id ? null : operator);
  };

  const getStatusBadge = (status: string | null | undefined) => {
    if (status === 'Active') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border bg-slate-100 text-slate-500 border-slate-200">
        <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
        Inactive
      </span>
    );
  };

  const getCertExpiryBadge = (expiryDate: string | null | undefined) => {
    if (!expiryDate) {
      return <span className="text-sm text-slate-400">N/A</span>;
    }
    const days = differenceInDays(parseISO(expiryDate), new Date());
    const formatted = formatAppDate(expiryDate);
    if (days < 0) {
      return (
        <span className="inline-flex items-center gap-1 text-sm text-red-600 font-medium">
          <AlertTriangle className="w-3.5 h-3.5" />
          {formatted}
        </span>
      );
    }
    if (days <= 30) {
      return (
        <span className="inline-flex items-center gap-1 text-sm text-amber-600 font-medium">
          <Clock className="w-3.5 h-3.5" />
          {formatted}
        </span>
      );
    }
    return <span className="text-sm text-slate-700">{formatted}</span>;
  };

  const SkeletonRow = () => (
    <tr>
      {Array.from({ length: 9 }).map((_, i) => (
        <td key={i} className="px-5 py-3.5">
          <div
            className="h-4 bg-slate-200 rounded animate-pulse"
            style={{ width: `${60 + Math.random() * 40}%` }}
          />
        </td>
      ))}
    </tr>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2
            className="text-lg font-bold text-slate-800"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Operator Management
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Manage weighbridge operators, qualifications and certifications
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-xs font-semibold flex items-center gap-1.5"
        >
          <UserPlus className="w-4 h-4" /> Add Operator
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, employee no, certificate..."
            className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <ThemedSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(String(e.target.value))}
            className="pl-9 pr-8 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm appearance-none bg-surface"
          >
            <option value="All">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </ThemedSelect>
        </div>
      </div>

      <div className="bg-surface rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50/80 border-b border-slate-100">
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Employee No
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Full Name
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Qualification
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Certificate No
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Issuing Body
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Certified Date
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Expiry Date
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Status
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Assigned Assets
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
              ) : operators.length === 0 ? (
                <tr>
                  <td colSpan={10}>
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center mb-4">
                        <Users className="w-8 h-8 text-slate-300" />
                      </div>
                      <h3 className="text-base font-semibold text-slate-700 mb-1">
                        No operators found
                      </h3>
                      <p className="text-sm text-slate-400 max-w-sm">
                        {searchTerm || statusFilter !== 'All'
                          ? 'Try adjusting your search or filter criteria'
                          : 'Add your first weighbridge operator to get started'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                operators.map((op) => (
                  <Fragment key={op.id}>
                    <tr
                      className={`hover:bg-primary-50/30 cursor-pointer transition-colors ${selectedOperator?.id === op.id ? 'bg-primary-50/40' : ''}`}
                      onClick={() => handleRowClick(op)}
                    >
                      <td className="px-5 py-3.5 text-sm text-slate-700 font-mono">
                        {op.employee_number}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-700 font-medium">
                        {op.full_name}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-700">
                        {qualificationLabel(op.qualification) || '-'}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-700 font-mono">
                        {op.certificate_number || '-'}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-700">
                        {op.issuing_body || '-'}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-700">
                        {op.certification_date ? formatAppDate(op.certification_date) : '-'}
                      </td>
                      <td className="px-5 py-3.5">{getCertExpiryBadge(op.expiry_date)}</td>
                      <td className="px-5 py-3.5">{getStatusBadge(op.status)}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-700 text-center">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
                          {op.assigned_assets?.length || 0}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(op);
                            }}
                            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                            title="Edit operator"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirm(op.id);
                            }}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                            title="Delete operator"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {selectedOperator?.id === op.id && (
                      <tr>
                        <td colSpan={10} className="bg-slate-50/60 px-5 py-4">
                          <div className="space-y-3">
                            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                              Assigned Weighbridges
                            </h4>
                            {op.assigned_assets && op.assigned_assets.length > 0 ? (
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                {op.assigned_assets.map((asset) => (
                                  <div
                                    key={asset.assignment_id}
                                    className="flex items-center gap-2 bg-surface px-3 py-2 rounded-lg border border-slate-200"
                                  >
                                    <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                    <div className="min-w-0">
                                      <p className="text-sm text-slate-700 font-medium truncate">
                                        {asset.asset_name || 'Unknown'}
                                      </p>
                                      {asset.assigned_date && (
                                        <p className="text-[11px] text-slate-400">
                                          Assigned: {formatAppDate(asset.assigned_date)}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-slate-400">
                                No weighbridges assigned to this operator
                              </p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-800">Delete Operator</h3>
                <p className="text-sm text-slate-500">This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-sm text-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-semibold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <OperatorModal
          operator={editingOperator}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
};
